import React from 'react';
import { Snackbar } from '@mui/material';
import { FaRegCopy } from 'react-icons/fa';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import DataGrid from '../../Component/DataGrid/DataGrid';
import UserAddress from '../UserDetails/UserAddress';
import ChargeBackService from '../../Services/ChargeBackService';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import { copyToClipboard } from '../../Component/Reusables/CopyToClipBoard';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { UserDataItem, ChargeBackInfoProps, ChargeBackInfoState } from './Interface';
import { TableColumn } from 'react-data-table-component';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import { ShowAlert } from '../../Component/ShowAlert';

// Main class for ChargeBackInfo component
class ChargeBackInfo extends React.Component<ChargeBackInfoProps, ChargeBackInfoState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    // Define the columns for the data table
    private tableColumns: TableColumn<UserDataItem>[] = [
        {
            name: 'User Name',
            selector: row => row.name || "-",
            sortable: true,
            id: "name",
            minWidth: "200px",
            wrap: true,
        },
        {
            name: 'Email',
            selector: row => row.email || "-",
            sortable: true,
            id: "email",
            minWidth: "230px",
            wrap: true,
        },
        {
            name: 'Mobile Number',
            selector: row => row.mobile_number || "-",
            sortable: true,
            id: "mobile_number",
            minWidth: "130px",
            wrap: true
        },
        {
            name: 'Persona Id',
            selector: row => row.personaId || "-",
            sortable: true,
            id: "personaId",
            minWidth: "280px",
            maxWidth: "300px",
            center: true,
            wrap: true,
            cell: (row) => (
                row.personaId ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <a
                            href={`https://app.withpersona.com/dashboard/inquiries/${row.personaId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {row.personaId}
                        </a>
                        <FaRegCopy
                            style={{ cursor: "pointer", color: "#007bff" }}
                            onClick={() => copyToClipboard(row.personaId, this.setState.bind(this))}
                            title="Copy to clipboard"
                        />
                    </div>
                ) : <span>-</span>
            ),
        },
        {
            name: 'Chargeback Against Id',
            selector: row => row.chargebackAgainstId || "-",
            sortable: true,
            id: "chargebackAgainstId",
            minWidth: "150px",
            center: true,
            wrap: true,
        },
        {
            name: 'Reference Number',
            selector: row => row.referenceNumber || "-",
            sortable: true,
            id: "referenceNumber",
            center: true,
            minWidth: "150px",
            maxWidth: "200px",
            wrap: true,
            cell: (row) => (
                row.referenceNumber ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        {row.referenceNumber && row.referenceNumber}
                        <FaRegCopy
                            style={{ cursor: "pointer", color: "#007bff" }}
                            onClick={() => copyToClipboard(row.referenceNumber, this.setState.bind(this))}
                            title="Copy to clipboard"
                        />
                    </div>
                ) : <span>-</span>
            ),
        },
        {
            name: 'Chargeback ID',
            selector: row => row.id || "-",
            sortable: true,
            id: "chargeback_id",
            minWidth: "150px",
            center: true,
            wrap: true,
        },
        {
            name: "External Id",
            selector: row => row.externalId || "-",
            sortable: true,
            id: "externalId",
            minWidth: "130px",
            center: true,
            wrap: true,
        },
        {
            name: "Amount",
            selector: row => row.amount || "-",
            sortable: true,
            id: "amount",
            minWidth: "130px",
            center: true,
            wrap: true,
        },
        {
            name: "Source Card ID",
            selector: row => row.sourceCardId || "-",
            sortable: true,
            id: "sourceCardId",
            minWidth: "130px",
            center: true,
            wrap: true,
        },
        {
            name: "Destination Account Id",
            selector: row => row.amount || "-",
            sortable: true,
            id: "amount",
            minWidth: "130px",
            center: true,
            wrap: true,
        },
        {
            name: 'Actions',
            cell: row => {
                const { chargeBack } = this.state;

                const popoverItems = [];

                if (chargeBack.isDownloadFileAllowed) {
                    popoverItems.push({ key: "download_file", label: "Download File" });
                }

                if (chargeBack.isSendEmailAllowd) {
                    popoverItems.push({ key: "send_email", label: "Send Email" });
                }

                // If no permitted actions, render nothing
                if (popoverItems.length === 0) {
                    return null;
                }

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={popoverItems}
                        onPopoverClick={(key_: string) => {
                            if (key_ === "download_file") {
                                this.handleDownloadFile(row);
                            } else if (key_ === "send_email") {
                                this.handleSendEmail(row);
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '100px'
        }
    ]

    constructor(props_: ChargeBackInfoProps) {
        super(props_);

        this.state = {
            accessToken: "",
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: "",
            selectedFilter: "",
            filterValue: "",
            userData: [],
            snackbarOpen: false,
            snackbarMessage: "",
            chargeBack: {
                isVisible: true,
                isDownloadFileAllowed: true,
                isSendEmailAllowd: true
            },
        }
    }

    handleSnackbarClose = () => {
        this.setState({ snackbarOpen: false });
    };

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.chargeback?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const chargeBack = permissionResponse.permissions.chargeback

                        this.setState({ chargeBack }, () => {
                            this.getChargeBackDetails();
                        });
                    } else {
                        this.getChargeBackDetails();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    public render() {
        return (
            <>
                <div className="body-sub-container table-container">
                    <DataGrid
                        title='Chargeback Details'
                        search='Chargeback Details'
                        onFilterChange={(selectedFilter: string, filterValue: string) => {
                            this.setState({
                                selectedFilter,
                                filterValue,
                                currentPage: 1,
                            }, () => this.getChargeBackDetails());
                        }}
                        defaultSearch={this.state.searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                currentPage: 1
                            }, () => {
                                this.getChargeBackDetails();
                            });
                        }}
                        columns={this.tableColumns}
                        data={this.state.userData}
                        onChangePage={(page: number, totalRows: number) => {
                            this.setState({
                                currentPage: page
                            }, () => {
                                this.getChargeBackDetails();
                            })
                        }}
                        onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                            this.setState({
                                currentRowsPerPage: currentRowsPerPage_,
                                currentPage: currentPage_
                            }, () => {
                                this.getChargeBackDetails();
                            })
                        }}
                        expandableRows={true}
                        expandedComponent={(data_: any) =>
                            <UserAddress data={[data_.data]} />
                        }
                        pagination
                        paginationTotalRows={this.state.totalRows}
                    //exportCsv={userTransactionReport.isDownloadVisible ? true : false}
                    //exportCsv={true}
                    />
                </div>
                <Snackbar
                    open={this.state.snackbarOpen}
                    autoHideDuration={3000}
                    onClose={this.handleSnackbarClose}
                    message={this.state.snackbarMessage}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                />
            </>
        )
    }

    private async getChargeBackDetails() {
        let pageIndex: number = (this.state.currentPage - 1);
        let pagination: any = {
            "pageNumber": pageIndex,
            "recordPerPage": this.state.currentRowsPerPage
        };

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText;
        }

        if (this.state.selectedFilter) {
            pagination[this.state.selectedFilter] = this.state.filterValue;
        }

        try {
            const response = await ChargeBackService.getChargeBackDetails(this.state.accessToken, pagination);

            if (response.ok) {
                const resp = await response.json();

                this.setState({
                    userData: Array.isArray(resp.chargebackList) ? resp.chargebackList : [],
                    totalRows: resp.pageCount ?? 0
                });
            }
        } catch (error) {
            console.error("An error occurred while fetching chargeback details:", error);
        }
    }

    private async handleDownloadFile(row_: UserDataItem) {
        try {
            const persona_inq_id = row_.personaId;

            const resp = await ChargeBackService.getFile(this.state.accessToken, persona_inq_id);

            if (resp?.ok) {
                // Get the blob from the response
                const blob = await resp.blob();

                // Create a download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'document.pdf'; // You can set a dynamic filename if needed
                document.body.appendChild(a);
                a.click();

                // Clean up
                window.URL.revokeObjectURL(url);
                a.remove();

                ShowAlert("PassportPay", "File has been downloaded.", "success")
            } else {
                throw new Error("File download response not OK");
            }
        } catch (error) {
            console.error("Error downloading file:", error);
            ShowAlert("Error", "An error occurred while downloading the file. Please try again.", "error");
        }
    }

    private async handleSendEmail(row_: UserDataItem) {
        try {
            const reference_Number = row_.referenceNumber;

            const resp = await ChargeBackService.sendEmail(this.state.accessToken, reference_Number);

            if (resp?.ok) {
                ShowAlert("PassportPay", "Email has been successfully sent.", "success")
            }
        } catch (error) {
            console.error("Error sending email:", error);
            ShowAlert("Error", "An error occurred while sending the email. Please try again.", "error");
        }
    }
}

export default ChargeBackInfo;